import axios from 'axios';
import { getTokensFromStorage, storeTokens, clearTokens } from '../utils/token';
import { getStoredYearOfBirth } from '../utils/sessionStorage';
import { v4 as uuidv4 } from 'uuid'; // Import uuid for generating new state

// Environment configuration
const API_BASE_URL = process.env.REACT_APP_MIRASOL_BACKEND_URL || 'http://localhost:8001';
const isTestMode = process.env.NODE_ENV === 'test';
const useNginxProxy = process.env.REACT_APP_USE_NGINX_PROXY === 'true';

// Create axios instance with default config
const api = axios.create({
  baseURL: useNginxProxy ? '/api' : `${API_BASE_URL}/api`,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add state parameter to request (This function might need review based on where state is expected)
const addStateToRequest = (config, tokenData) => {
  if (!config) {
    config = {};
  }

  if (!config.params) {
    config.params = {};
  }

  // Add state parameter from token if available
  // Note: State is now stored with tokens and should be sent in the initial checkin and refresh requests.
  // For other authenticated requests, the state might not be needed in query params,
  // as the backend can get it from the MiraSolCase linked by the JWT payload.
  // This function might be redundant or need adjustment depending on backend expectations.
  // For now, keeping it but noting potential redundancy for authenticated calls.
  if (tokenData && tokenData.state) {
    config.params.state = tokenData.state;
  } else {
    // console.warn('No state parameter found in token data'); // Suppress warning for now
  }

  return config;
};

// Create a mock API for test mode
const mockApi = {
  post: (url, data, config) => {
    console.log('🔵 TEST MODE: API Request', {
      method: 'POST',
      url,
      data,
      config
    });

    // Return a mock successful response
    return Promise.resolve({
      status: 200,
      data: {
        success: true,
        message: 'This is a mock response in test mode',
        request: { url, data, config }
      }
    });
  },

  get: (url, config) => {
    console.log('🔵 TEST MODE: API Request', {
      method: 'GET',
      url,
      config
    });

    // Return a mock successful response with sample data based on URL
    if (url.includes('/intake-forms')) {
      return Promise.resolve({
        status: 200,
        data: {
          success: true,
          form_id: 'patient_intake',
          submission_id: 'mock-submission-123',
          form_values: {
            hasDiabetes: false,
            hasHeartDisease: false,
            hasHighBloodPressure: true,
            hasAsthma: false,
            hasOtherConditions: false,
            otherConditionsText: '',
            currentMedications: 'Lisinopril 10mg daily',
            hasAllergies: true,
            allergiesText: 'Penicillin, Peanuts',
            reasonForVisit: 'Annual checkup',
            symptomsStarted: '',
            painLevel: '0',
            additionalInfo: ''
          }
        }
      });
    } else if (url.includes('/lhc-forms')) {
      return Promise.resolve({
        status: 200,
        data: {
          success: true,
          submission_id: 'mock-lhc-submission-123',
          form_values: {
            resourceType: "QuestionnaireResponse",
            status: "completed",
            authored: new Date().toISOString(),
            item: [
              {
                linkId: "/76435-7",
                answer: [{ valueString: "TEST-PATIENT-123" }]
              },
              // Reason for Visit section
              {
                linkId: "/questions",
                item: [
                  {
                    linkId: "/questions/question_category",
                    answer: [{ valueCoding: { code: "QUE-5", text: "Medical conditions" } }]
                  },
                  {
                    linkId: "/questions/question_text",
                    answer: [{ valueString: "Annual checkup and follow-up on hypertension" }]
                  },
                  {
                    linkId: "/questions/question_date",
                    answer: [{ valueDate: "2025-04-01" }]
                  },
                  {
                    linkId: "520127327318", // pain rating
                    answer: [{ valueInteger: 2 }]
                  },
                  {
                    linkId: "/questions/question_answer",
                    answer: [{ valueString: "Lower back pain" }]
                  }
                ]
              },
              // Medical Conditions section (multiple entries)
              {
                linkId: "/conditions",
                item: [
                  // First condition
                  {
                    linkId: "/conditions/condition",
                    answer: [{ valueCoding: { code: "38341003", text: "Hypertension" } }]
                  },
                  {
                    linkId: "/conditions/cond_status",
                    answer: [{ valueCoding: { code: "A", text: "Active" } }]
                  },
                  {
                    linkId: "/conditions/cond_started",
                    answer: [{ valueDate: "2020-06-15" }]
                  },
                  {
                    linkId: "/conditions/cond_comment",
                    answer: [{ valueString: "Controlled with medication" }]
                  }
                ]
              },
              {
                linkId: "/conditions",
                item: [
                  // Second condition
                  {
                    linkId: "/conditions/condition",
                    answer: [{ valueCoding: { code: "73211009", text: "Diabetes mellitus type 2" } }]
                  },
                  {
                    linkId: "/conditions/cond_status",
                    answer: [{ valueCoding: { code: "A", text: "Active" } }]
                  },
                  {
                    linkId: "/conditions/cond_started",
                    answer: [{ valueDate: "2022-03-10" }]
                  },
                  {
                    linkId: "/conditions/cond_comment",
                    answer: [{ valueString: "Diet controlled, monitoring blood sugar" }]
                  }
                ]
              },
              // Medications section (multiple entries)
              {
                linkId: "/medications",
                item: [
                  // First medication
                  {
                    linkId: "/medications/med_name",
                    answer: [{ valueCoding: { code: "29046", text: "Lisinopril" } }]
                  },
                  {
                    linkId: "/medications/med_strength",
                    answer: [{ valueCoding: { code: "10 mg", text: "10 mg" } }]
                  },
                  {
                    linkId: "/medications/med_status",
                    answer: [{ valueCoding: { code: "A", text: "Active" } }]
                  },
                  {
                    linkId: "/medications/med_started",
                    answer: [{ valueDate: "2020-07-01" }]
                  },
                  {
                    linkId: "/medications/med_instructions",
                    answer: [{ valueString: "Take once daily in the morning" }]
                  }
                ]
              },
              {
                linkId: "/medications",
                item: [
                  // Second medication
                  {
                    linkId: "/medications/med_name",
                    answer: [{ valueCoding: { code: "4124", text: "Metformin" } }]
                  },
                  {
                    linkId: "/medications/med_strength",
                    answer: [{ valueCoding: { code: "500 mg", text: "500 mg" } }]
                  },
                  {
                    linkId: "/medications/med_status",
                    answer: [{ valueCoding: { code: "A", text: "Active" } }]
                  },
                  {
                    linkId: "/medications/med_started",
                    answer: [{ valueDate: "2022-03-15" }]
                  },
                  {
                    linkId: "/medications/med_instructions",
                    answer: [{ valueString: "Take twice daily with meals" }]
                  }
                ]
              },
              // Allergies section (multiple entries)
              {
                linkId: "/allergies",
                item: [
                  // First allergy
                  {
                    linkId: "/allergies/allergy_name",
                    answer: [{ valueCoding: { code: "DRUG-CLASS-24", text: "Penicillin and Derivatives" } }]
                  },
                  {
                    linkId: "/allergies/allergy_reaction",
                    answer: [{ valueCoding: { code: "REAC-1", text: "Rash" } }]
                  },
                  {
                    linkId: "/allergies/allergy_severity",
                    answer: [{ valueCoding: { code: "SEV-2", text: "Moderate" } }]
                  }
                ]
              },
              {
                linkId: "/allergies",
                item: [
                  // Second allergy
                  {
                    linkId: "/allergies/allergy_name",
                    answer: [{ valueCoding: { code: "FOOD-9", text: "Peanut" } }]
                  },
                  {
                    linkId: "/allergies/allergy_reaction",
                    answer: [{ valueCoding: { code: "REAC-3", text: "Anaphylaxis" } }]
                  },
                  {
                    linkId: "/allergies/allergy_severity",
                    answer: [{ valueCoding: { code: "SEV-3", text: "Severe" } }]
                  }
                ]
              }
            ]
          }
        }
      });
    }

    return Promise.resolve({
      status: 200,
      data: {
        success: true,
        message: 'This is a mock response in test mode',
        request: { url, config }
      }
    });
  }
};

// Use the appropriate API based on test mode
const apiClient = isTestMode ? mockApi : api;

// Cache for token verification to prevent duplicate requests
export const tokenVerificationCache = {
  lastVerifiedToken: null,
  lastVerificationResult: null,
  lastVerificationTime: null
};

// Add request interceptor for authentication
api.interceptors.request.use(
  (config) => {
    // Retrieve tokens from localStorage
    const storedTokens = getTokensFromStorage();

    // If PIN-JWT exists, add it to the Authorization header
    if (storedTokens && storedTokens.pin_jwt) {
      config.headers.Authorization = `Bearer ${storedTokens.pin_jwt}`;
      
      // Only log once per session or when token changes
      if (!tokenVerificationCache.lastVerifiedToken ||
          tokenVerificationCache.lastVerifiedToken !== storedTokens.pin_jwt) {
        console.log('Adding PIN-JWT to Authorization header.');
      }
    } else {
      // Only log once per session
      if (!tokenVerificationCache.lastVerifiedToken) {
        console.log('No PIN-JWT found in storage, skipping Authorization header.');
      }
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Flag to prevent infinite refresh loops
let isRefreshing = false;
// Queue for requests that need to be retried after token refresh
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};


// Add response interceptor for error handling and token refresh
api.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => { // Made the interceptor async to handle token refresh
    const originalRequest = error.config;

    // Handle common errors
    if (error.response) {
      // Server responded with an error status
      console.error('API Error:', error.response.status, originalRequest.url, error.response.data);

      // Handle specific status codes
      switch (error.response.status) {
        case 401: {
          // Unauthorized - token expired or invalid
          console.error('Authentication error: 401 Unauthorized');

          // Check for scope/permission errors
          const errorDetail = error.response.data.detail || error.response.data.error || '';
          if (errorDetail.includes('do not have the required permissions')) {
            console.error('Scope permission error detected');
            // Return error with metadata for component to display using alertMessage
            return Promise.reject({
              ...error, userMessage: errorDetail, isPermissionError: true
            });
          }

          // Check if the error is due to an expired PIN-JWT and not the refresh endpoint itself
          // Also, ensure we are not already refreshing and the request is not to the refresh endpoint
          if (error.response.data.error === 'expired_token' && !isRefreshing && originalRequest.url !== '/patient/refresh-token/') {
              isRefreshing = true;
              console.log('Attempting to refresh token...');

              const storedTokens = getTokensFromStorage();

              if (storedTokens && storedTokens.refresh_token && storedTokens.state && storedTokens.case_id) {
                  try {
                      // Generate a new state for the refresh request
                      const new_state = uuidv4(); // Use uuidv4

                      // Retrieve the stored YOB
                      const storedYob = getStoredYearOfBirth(storedTokens.case_id);

                      if (!storedYob) {
                          console.error('YOB not found in storage for token refresh.');
                          clearTokens(); // Clear tokens if YOB is missing
                          processQueue(new Error('YOB missing for token refresh'));
                          // TODO: Redirect to start of flow or show a critical error
                          return Promise.reject(error); // Reject the original request
                      }

                      // Call the refresh token endpoint
                      const refreshResponse = await patientApi.refreshToken(
                          storedTokens.refresh_token,
                          storedTokens.state, // Use the current state as prior_state
                          storedYob, // Include the stored YOB
                          new_state // Pass the new state
                      );

                      if (refreshResponse.status === 200 && refreshResponse.data.pin_jwt && refreshResponse.data.refresh_token && refreshResponse.data.state) {
                          // Token refresh successful - store new tokens and state
                          storeTokens(
                              refreshResponse.data.pin_jwt,
                              refreshResponse.data.refresh_token,
                              refreshResponse.data.state,
                              storedTokens.case_id // Keep the same case_id
                          );
                          console.log('Token refresh successful. New tokens stored.');

                          // Retry the original request with the new token
                          originalRequest.headers.Authorization = `Bearer ${refreshResponse.data.pin_jwt}`;
                          // Also update state parameter in the original request if needed
                          // originalRequest.params.state = refreshResponse.data.state; // If state is in query params

                          // Process the failed queue with the new token
                          processQueue(null, refreshResponse.data.pin_jwt);

                          // Return the retried request promise
                          return api(originalRequest);

                      } else {
                          // Refresh token endpoint returned an error or invalid response
                          console.error('Token refresh failed:', refreshResponse.data);
                          clearTokens(); // Clear tokens on refresh failure
                          processQueue(new Error('Token refresh failed')); // Reject queued requests
                          // TODO: Redirect to start of flow or show a critical error
                      }
                  } catch (refreshError) {
                      console.error('Error during token refresh API call:', refreshError);
                      clearTokens(); // Clear tokens on refresh failure
                      processQueue(refreshError); // Reject queued requests
                      // TODO: Redirect to start of flow or show a critical error
                  } finally {
                      isRefreshing = false;
                  }
              } else {
                  // No refresh token or state or case_id found in storage
                  console.error('No refresh token, state, or case_id found in storage for refresh.');
                  clearTokens(); // Clear any incomplete tokens
                  // TODO: Redirect to start of flow or show a critical error
              }

              // If we are refreshing, queue the original request
              return new Promise(function(resolve, reject) {
                  failedQueue.push({ resolve, reject });
              });

          } else if (originalRequest.url === '/patient/refresh-token/') {
              // If the 401 is from the refresh endpoint itself, the refresh token is invalid/expired
              console.error('Refresh token is invalid or expired.');
              clearTokens(); // Clear tokens
              // TODO: Redirect to start of flow or show a critical error
          }

          break;
        }
        case 403: {
          // Forbidden - check for scope/permission errors
          const forbiddenDetail = error.response.data.detail || error.response.data.error || '';
          if (forbiddenDetail.includes('do not have the required permissions')) {
            console.error('Scope permission error detected (403)');
            // Return error with metadata for component to display using alertMessage
            return Promise.reject({
              ...error, userMessage: forbiddenDetail, isPermissionError: true
            });
          }
          console.error('Permission denied');
          break;
        }
        case 404:
          // Not found
          console.error('Resource not found');
          break;
        case 500:
          // Server error
          console.error('Server error');
          break;
        default:
          // Other errors
          break;
      }
    } else if (error.request) {
      // Request was made but no response received
      console.error('No response received:', error.request);
    } else {
      // Error in setting up the request
      console.error('Request error:', error.message);
    }

    return Promise.reject(error);
  }
);

// API endpoints
export const patientApi = {
  initiateVisit: (patientData) => {
    // This function calls the backend endpoint that initiates a Case (for demo only).
    return apiClient.post('/admin/initiate-visit/', patientData);
  },
  // Generate patient token (This endpoint is called by YearOfBirthVerification,
  // it does NOT need the PIN-JWT in the header as it's the first step to get one)
  // The interceptor will add it if it exists, but the backend should ignore it for this endpoint.
  initiateCheckInRequest: (patientData) => { // Renamed function
    // This function calls the backend endpoint that initiates a patient check-in.
    return apiClient.post('/patient/checkin/', patientData); // Corrected endpoint
  },

  // Verify token (updated to use the new backend endpoint)
  // This is called by decodePinJwt in utils/token.js, so it doesn't need the interceptor's JWT.
  // The interceptor will add it, but the backend verify endpoint should handle it.
  verifyToken: (token) => {
    return apiClient.post('/patient/verify-token/', { token }); // Updated endpoint
  },

  // Refresh token endpoint
  refreshToken: (refreshToken, priorState, yob, newState) => {
      const payload = {
          refresh_token: refreshToken,
          prior_state: priorState,
          yob: yob,
          new_state: newState
      };
      return apiClient.post('/patient/refresh-token/', payload); // Updated endpoint
  },

  // Create Zoom session with case information
  createZoomSession: (sessionData) => {
    return apiClient.post('/zoom/sessions/create/', sessionData);
  },

  // Get token for existing Zoom session
  getZoomSessionToken: (sessionData) => {
    return apiClient.post('/zoom/session/token/', sessionData);
  },

  // Unified function to fetch form data
  getFormData: (patientId, formId, submissionId = null, formVersion = null, tokenData = {}) => {
    // tokenData is passed here but the interceptor adds the JWT.
    // The patientId and formId are used in the URL.
    // The backend will use the JWT payload to verify patient/case/health system access.
    const config = {
      params: {}
    };

    // Add submissionId and formVersion to query parameters if provided
    if (submissionId) {
      config.params.submissionId = submissionId;
    }

    if (formVersion) {
      // Convert version string like "1.2" to integer 1
      if (typeof formVersion === 'string' && formVersion.includes('.')) {
        const versionParts = formVersion.split('.');
        config.params.form_version = parseInt(versionParts[0], 10);
        console.log(`Converting form version from ${formVersion} to ${config.params.form_version}`);
      } else {
        config.params.form_version = formVersion;
      }
    }

    return apiClient.get(`/patient/${patientId}/forms/${formId}/data/`, config); // Keep original endpoint since it helps logging/troubleshooting
  },

  // Unified function to submit form data
  submitFormData: (patientId, formId, formData, submissionId = null, updateData = false, formVersion = null, tokenData = {}) => {
    // tokenData is passed here but the interceptor adds the JWT.
    // The patientId and formId are used in the payload and URL.
    // The backend will use the JWT payload to verify patient/case/health system access.
    const config = addStateToRequest({}, tokenData); // State might not be needed here

    // Prepare request payload
    const payload = {
      // patient_id: patientId, // Patient ID should come from JWT in backend
      form_id: formId,
      form_values: formData, // Use form_values for the actual form data
      submitted_at: new Date().toISOString(), // Timestamp for this submission/update
      submission_id: submissionId, // Include submissionId if updating
      update_data: updateData, // Flag to indicate if this is an update
    };

    // Convert version string like "1.2" to integer 1 if needed
    if (formVersion) {
      if (typeof formVersion === 'string' && formVersion.includes('.')) {
        const versionParts = formVersion.split('.');
        payload.form_version = parseInt(versionParts[0], 10);
        console.log(`Converting form version from ${formVersion} to ${payload.form_version}`);
      } else {
        payload.form_version = formVersion;
      }
    }

    // Add state to payload if available (although state is typically in query params for GET)
    // For POST body, it might be included in the main payload if backend expects it there
    // Based on addStateToRequest, it's added to config.params, which is for query string.
    // Let's keep it consistent with other POSTs and add to payload if needed by backend.
    if (tokenData && tokenData.state) {
       // Decide if state should be in payload or query params for POST
       // Sticking to query params for now as per addStateToRequest
    }


    // Assuming the backend endpoint is /api/patient/{patient_id}/forms/{form_id}/submissions/ /api is added by interceptor
    return apiClient.post(`/patient/${patientId}/forms/${formId}/submissions/`, payload, config);
  },

  // Notify backend that check-in is complete
  completeCheckIn: (patientId, checkInData, tokenData = {}) => {
    const config = addStateToRequest({}, tokenData);

    // Log skipped forms if available
    if (checkInData?.skippedForms && checkInData.skippedForms.length > 0) {
      console.log(`Sending ${checkInData.skippedForms.length} skipped forms to backend:`, checkInData.skippedForms);
    }

    // Prepare the request data with skipped forms data
    const requestData = {
      ...checkInData,
      patient_id: patientId,
      timestamp: new Date().toISOString(),
      status: 'completed'
    };

    // Send the data to the backend
    return apiClient.post('/patient/checkin/complete/', requestData, config);
  },

  // Submit insurance information (Keep existing function for now)
  submitInsurance: (formData, tokenData = {}) => {
    const config = addStateToRequest({
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    }, tokenData);

    // If formData is FormData object, append state
    if (formData instanceof FormData && tokenData && tokenData.state) {
      formData.append('state', tokenData.state);
    }

    return apiClient.post('/patient/insurance-verification/', formData, config);
  },

  // Get all insurance packages for a patient
  getPatientInsurancePackages: (patientId, tokenData = {}) => {
    const config = addStateToRequest({}, tokenData);
    return apiClient.get(`/patient/${patientId}/insurance/`, config)
      .then(response => {
        console.log('Insurance API response:', response.data);
        return response.data.insurance_data || [];
      })
      .catch(error => {
        console.error('Error fetching insurance packages:', error);
        
        // If it's a 404, return an empty array instead of throwing an error
        if (error.response && error.response.status === 404) {
          console.log('No insurance packages found for patient, returning empty array');
          return [];
        }
        
        // For other errors, re-throw so the component can handle it
        throw error;
      });
  },

  // Update an existing insurance package
  updateInsurancePackage: (insuranceId, formData, tokenData = {}) => {
    const config = addStateToRequest({
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    }, tokenData);

    // If formData is FormData object, append state
    if (formData instanceof FormData && tokenData && tokenData.state) {
      formData.append('state', tokenData.state);
    }

    return apiClient.put(`/patient/insurance/${insuranceId}/`, formData, config);
  },

  // Delete an insurance package
  deleteInsurancePackage: (insuranceId, tokenData = {}) => {
    const config = addStateToRequest({}, tokenData);
    return apiClient.delete(`/patient/insurance/${insuranceId}/`, config);
  },

  // Set the active status of an insurance package
  setInsurancePackageActive: (insuranceId, isActive, tokenData = {}) => {
    const config = addStateToRequest({}, tokenData);
    return apiClient.patch(`/patient/insurance/${insuranceId}/active/`, { is_active: isActive }, config);
  },

  // Verify insurance as unchanged
  verifyInsuranceUnchanged: (patientId, caseId, tokenData = {}) => {
    const config = addStateToRequest({}, tokenData);
    return apiClient.post(`/patient/${patientId}/insurance/verify-unchanged/`, { case_id: caseId }, config);
  },

  // Unified forms API with parameter-based filtering
  getAllForms: (tokenData = {}) => {
    // Get lightweight list of all available forms (no parameters)
    const config = addStateToRequest({}, tokenData);
    return apiClient.get('/forms/', config);
  },
  // Get specific form with all versions and schemas
  getFormDetails: (formId, tokenData = {}) => {
    const config = addStateToRequest({
      params: { form_id: formId }
    }, tokenData);
    return apiClient.get('/forms/', config);
  },
  // Get specific submission data only
  getSubmissionDetails: (submissionId, tokenData = {}) => {
    const config = addStateToRequest({
      params: { submission_id: submissionId }
    }, tokenData);
    return apiClient.get('/forms/', config);
  },
  // Get all submissions for a patient
  getPatientSubmissions: (patientId, tokenData = {}) => {
    const config = addStateToRequest({
      params: { patient_id: patientId }
    }, tokenData);
    return apiClient.get('/forms/', config);
  },

  // CRUD operations for patient session status
  getPatientStatus: (caseId, tokenData = {}) => {
    const config = addStateToRequest({}, tokenData);
    return apiClient.get(`/patient/${caseId}/status/`, config);
  },
  updatePatientStatus: (caseId, sessionState, tokenData = {}) => {
    const config = addStateToRequest({}, tokenData);
    return apiClient.post(`/patient/${caseId}/status/`, { 
      sessionState, 
      sessionType: 'mira' // Specify this is a patient (mira) session
    }, config);
  },
  clearPatientStatus: (caseId, tokenData = {}) => {
    const config = addStateToRequest({}, tokenData);
    return apiClient.delete(`/patient/${caseId}/status/`, config);
  },
  getPatientDetails: (patientId, tokenData = {}) => {
    const config = addStateToRequest({}, tokenData);
    return apiClient.get(`/patient/${patientId}/details/`, config);
  },
};

// Create a separate admin API client with token authentication
export const adminApi = axios.create({
  baseURL: useNginxProxy ? '/api' : `${API_BASE_URL}/api`,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add request interceptor for adminApi authentication (using API token)
adminApi.interceptors.request.use(
  (config) => {
    // Retrieve admin token from localStorage
    const adminToken = localStorage.getItem('admin_api_token');
    // console.log('Retrieved admin_api_token from localStorage:', adminToken); 

    // If admin token exists, add it to the Authorization header
    if (adminToken) {
      config.headers.Authorization = `Bearer ${adminToken}`;
    } else {
      console.log('No Admin API token found in storage, skipping Authorization header.'); // Keep this log
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add response interceptor for adminApi error handling
adminApi.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    const originalRequest = error.config;
    // Handle common errors
    if (error.response) {
      console.error('Admin API Error:', error.response.status, originalRequest.url, error.response.data);
      // Handle specific status codes
      switch (error.response.status) {
        case 401:
        case 403: {
          // Unauthorized/Forbidden - token expired, invalid, or insufficient permissions

          const adminErrorDetail = error.response.data.detail || error.response.data.error || '';

          // Check for inactive token error (highest priority)
          if (adminErrorDetail.includes('API Token is inactive') || adminErrorDetail.includes('token is inactive')) {
            // Clear all auth data
            localStorage.removeItem('admin_api_token');
            localStorage.removeItem('admin_tokens');
            sessionStorage.clear();
            // Redirect to login with a clear message
            window.location.href = '/admin/login?session_expired=true';
            return Promise.reject(new Error('Your session has expired. Please log in again.'));
          }

          // Check for scope/permission errors
          if (adminErrorDetail.includes('do not have the required permissions')) {
            console.error('Scope permission error detected (Admin API)');
            // Return error with metadata for component to display using alertMessage
            return Promise.reject({
              ...error, userMessage: adminErrorDetail, isPermissionError: true
            });
          }

          // Check if this is a token expiration issue
          const adminToken = localStorage.getItem('admin_api_token');
          const storedTokens = localStorage.getItem('admin_tokens');
          if (adminToken && storedTokens) {
            try {
              const parsedTokens = JSON.parse(storedTokens);
              // Check if token has expired
              if (parsedTokens.expires_at && new Date(parsedTokens.expires_at) <= new Date()) {
                // Clear expired tokens
                localStorage.removeItem('admin_api_token');
                localStorage.removeItem('admin_tokens');
                // Redirect to login page
                window.location.href = '/admin/login?session_expired=true';
                return Promise.reject(new Error('Admin token expired. Please log in again.'));
              }
            } catch (parseError) {
              // Clear corrupted tokens
              localStorage.removeItem('admin_api_token');
              localStorage.removeItem('admin_tokens');
              window.location.href = '/admin/login';
              return Promise.reject(new Error('Invalid token data. Please log in again.'));
            }
          } else {
            window.location.href = '/admin/login';
            return Promise.reject(new Error('No admin token found. Please log in.'));
          }
          // If we reach here, token exists and hasn't expired, so it's a permissions issue
          break;
        }
        case 404:
          break;
        case 500:
          break;
        default:
          console.error('Admin API error:', error.response.status, originalRequest.url);
          break;
      }
    } else if (error.request) {
      console.error('Admin API - No response received:', error.request);
    } else {
      console.error('Admin API - Request error:', error.message);
    }
    return Promise.reject(error);
  }
);

// Admin API endpoints - define methods directly on the adminApi instance
adminApi.getHealthSystems = (filters = {}) => adminApi.get('/admin/health-systems/', { params: filters });
adminApi.getHealthSystemDetails = (healthSystemId) => adminApi.get(`/admin/health-system/${healthSystemId}/`);
adminApi.getHealthSystemCases = (healthSystemId) => adminApi.get(`/admin/health-system/${healthSystemId}/cases/`);
adminApi.getHealthSystemUsers = (healthSystemId) => adminApi.get(`/admin/health-system/${healthSystemId}/users/`);
adminApi.getHealthSystemProviders = (healthSystemId) => adminApi.get(`/admin/health-system/${healthSystemId}/providers/`);
adminApi.getHealthSystemPatients = (healthSystemId) => adminApi.get(`/admin/health-system/${healthSystemId}/patients/`);
adminApi.searchPatients = (healthSystemId, searchQuery, page = 1, pageSize = 20) => {
  const params = { search: searchQuery, page: page, page_size: pageSize
  };
  if (healthSystemId) { return adminApi.get(`/admin/health-system/${healthSystemId}/patients/search/`, { params }); } else { return adminApi.get('/admin/patient/', { params }); }
};
adminApi.getSpecialties = (filters = {}) => adminApi.get('/admin/specialties/', { params: filters });
adminApi.getCases = (filters = {}) => {
  // Use lightweight 'list' view by default for performance, unless 'view' is explicitly specified
  const params = { view: 'list', ...filters };
  return adminApi.get('/admin/cases/', { params });
};
adminApi.getProviders = (filters = {}) => adminApi.get('/admin/providers/', { params: filters });
adminApi.getProviderDetails = (providerId) => adminApi.get(`/admin/providers/${providerId}/`);
adminApi.updateProviderDetails = (providerId, providerData) => adminApi.put(`/admin/providers/${providerId}/`, providerData);
adminApi.getProviderCases = (providerId) => adminApi.get(`/admin/providers/${providerId}/cases/`);
adminApi.getAllForms = (filters = {}) => adminApi.get('/admin/forms/', { params: filters });
adminApi.createForm = (formData) => adminApi.post('/admin/forms/', formData);
adminApi.getFormDetails = (formId, version = null) => {
  const params = version ? { version } : {};
  return adminApi.get(`/admin/forms/${formId}/`, { params });
};
adminApi.updateForm = (formId, formData, version = null) => {
  const params = version ? { version } : {};
  return adminApi.put(`/admin/forms/${formId}/`, formData, { params });
};
adminApi.deleteForm = (formId, version = null) => {
  const params = version ? { version } : {};
  return adminApi.delete(`/admin/forms/${formId}/`, { params });
};
adminApi.getFormTypes = (filters = {}) => adminApi.get('/admin/form-types/', { params: filters });
adminApi.initiateVisit = (visitData) => adminApi.post('/admin/initiate-visit/', visitData); // This seems redundant with patientApi.initiateVisit - review needed
adminApi.getUsers = (filters = {}) => adminApi.get('/admin/users/', { params: filters }); // Assuming an admin endpoint for users
adminApi.createUser = (userData) => adminApi.post('/admin/users/', userData); // Assuming an admin endpoint for creating users
adminApi.getUserDetails = (userId) => adminApi.get(`/admin/users/${userId}/`); // Assuming an admin endpoint for user details
adminApi.updateUser = (userId, userData) => adminApi.put(`/admin/users/${userId}/`, userData); // Assuming an admin endpoint for updating users
adminApi.deleteUser = (userId) => adminApi.delete(`/admin/users/${userId}/`); // Assuming an admin endpoint for deleting users

// Functions for case details and audit logs
adminApi.getCaseDetails = (caseId) => adminApi.get(`/admin/cases/${caseId}/`);
adminApi.getCaseAuditLogs = (caseId, filters = {}) => adminApi.get(`/admin/cases/${caseId}/audit-logs/`, { params: filters });
adminApi.getCaseAudioSessions = (caseId) => adminApi.get(`/healthcare/cases/${caseId}/audio-sessions/`);
adminApi.downloadAudioFile = (data) => adminApi.post(`/healthcare/cases/${data.case_id}/audio-sessions/download/`, data);
adminApi.getPatientDetails = (patientId) => adminApi.get(`/admin/patient/${patientId}/`);
adminApi.getPatientCases = (patientId) => adminApi.get(`/admin/patient/${patientId}/cases/`);
adminApi.getPatientHealthSystems = (patientId) => adminApi.get(`/admin/patient/${patientId}/health-systems/`);
adminApi.getPatientZoomVisits = (patientId) => adminApi.get(`/admin/patient/${patientId}/visits/`);
adminApi.getPatientAccessLogs = (patientId, filters = {}) => adminApi.get(`/admin/patient/${patientId}/logs/`, { params: filters });
adminApi.unlockShortUrl = (data) => {
 return adminApi.post('/admin/shorturl/manage/', data);
};

// ======================================================================
// VCX EVENTS & VOICE AI AGENTS
// ======================================================================

// VCX Events API (auto-scoped)
adminApi.getVCXEvents = (params = {}) => adminApi.get(`/admin/vcx-events/`, { params });
adminApi.getVCXEvent = (eventId) => adminApi.get(`/admin/vcx-events/${eventId}/`);
adminApi.getVCXEventSummary = (eventId) => adminApi.get(`/admin/vcx-events/${eventId}/`, { params: { response_format: 'summary' } });
adminApi.createVCXEvent = (data) => adminApi.post(`/admin/vcx-events/`, data);
adminApi.updateVCXEvent = (eventId, data) => adminApi.patch(`/admin/vcx-events/${eventId}/`, data);
adminApi.deleteVCXEvent = (eventId, permanent = false) => {
  const params = permanent ? { permanent: 'true' } : {};
  return adminApi.delete(`/admin/vcx-events/${eventId}/`, { params });
};
adminApi.restoreVCXEvent = (eventId) => adminApi.patch(`/admin/vcx-events/${eventId}/`, { is_active: true });
adminApi.getVCXEventsStats = (params = {}) => adminApi.get(`/admin/vcx-events/stats/`, { params });
adminApi.getVCXEventsByPatient = (healthSystemId, patientId, params = {}) => adminApi.get(`/admin/health-systems/${healthSystemId}/vcx-events/patient/${patientId}/`, { params });
adminApi.getVCXEventsByCase = (healthSystemId, caseId, params = {}) => adminApi.get(`/admin/health-systems/${healthSystemId}/vcx-events/case/${caseId}/`, { params });

// VCX Event Activities API (auto-scoped - no health_system_id required)
adminApi.getVCXEventActivities = (eventId, params = {}) => adminApi.get(`/admin/vcx-events/${eventId}/activities/`, { params });
adminApi.getVCXEventActivity = (eventId, activityId, params = {}) => adminApi.get(`/admin/vcx-events/${eventId}/activities/${activityId}/`, { params });
adminApi.createVCXEventActivity = (eventId, data) => adminApi.post(`/admin/vcx-events/${eventId}/activities/`, data);
adminApi.updateVCXEventActivity = (eventId, activityId, data) => adminApi.patch(`/admin/vcx-events/${eventId}/activities/${activityId}/`, data);
adminApi.deleteVCXEventActivity = (eventId, activityId) => adminApi.delete(`/admin/vcx-events/${eventId}/activities/${activityId}/`);

// Communication Actions API (SMS, Email, Teams, Calls)
adminApi.sendSMS = (data) => adminApi.post('/admin/communications/sms/', data);
adminApi.sendEmail = (data) => adminApi.post('/admin/communications/email/', data);
adminApi.sendTeamsMessage = (data) => adminApi.post('/admin/communications/teams/', data);
adminApi.initiateCall = (data) => adminApi.post('/admin/communications/call/', data);

// Feedback API
adminApi.submitEventFeedback = (eventId, data) => adminApi.patch(`/admin/vcx-events/${eventId}/`, { event_metadata: { feedback: data } });
adminApi.submitSessionFeedback = (data) => adminApi.post('/admin/feedback/session/', data);

// VoiceAI Agents API (auto-scoped)
adminApi.getVoiceAIAgents = (params = {}) => adminApi.get(`/admin/voiceai-agents/`, { params });
adminApi.createVoiceAIAgent = (data) => adminApi.post(`/admin/voiceai-agents/`, data);
adminApi.getVoiceAIAgent = (agentId) => adminApi.get(`/admin/voiceai-agents/${agentId}/`);
adminApi.updateVoiceAIAgent = (agentId, data) => adminApi.patch(`/admin/voiceai-agents/${agentId}/`, data);
adminApi.deleteVoiceAIAgent = (agentId) => adminApi.delete(`/admin/voiceai-agents/${agentId}/`);

// ======================================================================
// USER AUTHENTICATION - TWO-STEP FLOW
// ======================================================================

// Step 1: Check user type by email/username (returns user_type and auth_endpoint)
adminApi.checkUserType = (email) => {
  const unauthenticatedAxios = axios.create({
    baseURL: useNginxProxy ? '/api' : `${API_BASE_URL}/api`,
    headers: { 'Content-Type': 'application/json' },
  });
  return unauthenticatedAxios.post('/auth/', { email });
};

// Step 2: Login with username/password at the appropriate endpoint
adminApi.userLogin = (username, password, authEndpoint, healthSystemId = null) => {
  const unauthenticatedAxios = axios.create({
    baseURL: useNginxProxy ? '/api' : `${API_BASE_URL}/api`,
    headers: { 'Content-Type': 'application/json' },
  });
  const payload = { username, password };
  if (healthSystemId) {
    payload.health_system_id = healthSystemId;
  }
  return unauthenticatedAxios.post(authEndpoint, payload);
};

// Legacy: Admin API for token generation (used by InitiateVisitPage)
// This endpoint uses Basic Auth, not the Bearer token interceptor
// DEPRECATED - Use adminApi.checkUserType() + adminApi.userLogin() for user authentication

// Fetch all note templates for admin
adminApi.fetchAdminNoteTemplates = () => {
  return adminApi.get('/admin/note/templates/'); 
};

// Core Server Credentials Management Functions
adminApi.getCoreServerCredentials = () => adminApi.get('/admin/token/system/list/');
adminApi.createCoreServerCredential = (credentialData) => adminApi.post('/admin/token/system/create/', credentialData);
adminApi.updateCoreServerCredential = (credentialId, updateData) => adminApi.put(`/admin/token/system/${credentialId}/`, updateData);
adminApi.deleteCoreServerCredential = (credentialId) => adminApi.delete(`/admin/token/system/${credentialId}/`);
adminApi.generateCoreServerApiToken = (tokenData) => adminApi.post('/admin/token/system/', tokenData);

// API Token Management Functions
adminApi.verifyLoginToken = (apiToken) => adminApi.post('/admin/token/system/verify/', { api_token: apiToken });
adminApi.getApiTokens = (filters = {}) => adminApi.get('/admin/token/system/api/list/', { params: filters });
adminApi.activateApiToken = (tokenId) => adminApi.patch(`/admin/token/${tokenId}/activate/`);
adminApi.deactivateApiToken = (tokenId) => adminApi.patch(`/admin/token/${tokenId}/deactivate/`);
adminApi.deleteApiToken = (tokenId) => adminApi.delete(`/admin/token/${tokenId}/delete/`);

// Core Server User Token Management Functions
adminApi.manageCoreServerUserTokens = (coreServerUserId, action) => adminApi.post(`/admin/token/system/${coreServerUserId}/manage/`, { action });

// Audit Logs Functions
adminApi.getAuditLogs = (filters = {}) => adminApi.get('/admin/audit-logs/', { params: filters });
adminApi.getMfaUserAccessLogs = (mfaUserId, filters = {}) => adminApi.get(`/admin/mfa/users/${mfaUserId}/logs/`, { params: filters });
adminApi.getUserAuditLogs = (userType, userId, filters = {}) => adminApi.get(`/admin/users/${userType}/${userId}/audit-logs/`, { params: filters });

// Sales Lead APIs
adminApi.getSalesLeads = (filters = {}) => adminApi.get('/admin/sales/leads/', { params: filters });
adminApi.getSalesLead = (leadId) => adminApi.get(`/admin/sales/leads/${leadId}/`);
adminApi.createSalesLead = (leadData) => adminApi.post('/admin/sales/leads/', leadData);
adminApi.updateSalesLead = (leadId, leadData) => adminApi.patch(`/admin/sales/leads/${leadId}/`, leadData);
adminApi.deleteSalesLead = (leadId) => adminApi.delete(`/admin/sales/leads/${leadId}/`);
adminApi.convertLeadToOpportunity = (leadId) => adminApi.post(`/admin/sales/leads/${leadId}/convert/`);

// Sales Opportunity APIs (Previously called Prospects)
adminApi.getSalesOpportunities = (filters = {}) => adminApi.get('/admin/sales/opportunities/', { params: filters });
adminApi.getSalesOpportunity = (opportunityId) => adminApi.get(`/admin/sales/opportunities/${opportunityId}/`);
adminApi.createSalesOpportunity = (opportunityData) => adminApi.post('/admin/sales/opportunities/', opportunityData);
adminApi.updateSalesOpportunity = (opportunityId, opportunityData) => adminApi.patch(`/admin/sales/opportunities/${opportunityId}/`, opportunityData);
adminApi.deleteSalesOpportunity = (opportunityId) => adminApi.delete(`/admin/sales/opportunities/${opportunityId}/`);

// AI Agent APIs
adminApi.getRetellAgents = () => adminApi.get('/admin/sales/agents/');
adminApi.getRetellPhoneNumbers = () => adminApi.get('/admin/sales/phone-numbers/');
adminApi.getRetellCalls = (filterData) => adminApi.post('/admin/sales/agents/calls/list/', filterData);
adminApi.getRetellCallStatus = (callId) => adminApi.get(`/admin/sales/agents/calls/${callId}/`);
adminApi.getRetellCallDetails = (callId) => adminApi.get(`/admin/sales/agents/calls/${callId}/details/`);
adminApi.initiateRetellCall = (callData) => adminApi.post('/admin/sales/agents/call/initiate/', callData);

// Call Queue APIs
adminApi.getCallQueue = (filters = {}) => adminApi.get('/admin/sales/call-queue/', { params: filters });
adminApi.getCallQueueStats = () => adminApi.get('/admin/sales/call-queue/stats/');
adminApi.getCallQueueItem = (callId) => adminApi.get(`/admin/sales/call-queue/${callId}/`);
adminApi.updateCallQueueItem = (callId, data) => adminApi.patch(`/admin/sales/call-queue/${callId}/`, data);
adminApi.cancelCallQueueItem = (callId, reason) => adminApi.delete(`/admin/sales/call-queue/${callId}/`, { data: { reason } });
adminApi.reorderCallQueue = (calls) => adminApi.post('/admin/sales/call-queue/bulk/reorder/', { calls });

// Sales Settings APIs
// New naming (aligned with backend LeadOpportunityStatus model)
adminApi.getLeadOpportunityStatuses = () => adminApi.get('/admin/sales/settings/statuses/');
adminApi.getPriorities = () => adminApi.get('/admin/sales/settings/priorities/');
adminApi.getActivityTypes = () => adminApi.get('/admin/sales/settings/activity-types/');
adminApi.getOutcomeTypes = () => adminApi.get('/admin/sales/settings/outcome-types/');
adminApi.getLeadSources = () => adminApi.get('/admin/sales/settings/lead-sources/');
adminApi.getLeadTypes = () => adminApi.get('/admin/sales/settings/lead-types/');

// Outreach Activity APIs
adminApi.getOutreachActivities = (filters = {}) => {
  // Entity-based filtering (entity_id + entity_type)
  return adminApi.get('/admin/sales/outreach-activities/', { params: filters });
};

// Helper methods for clearer API usage
adminApi.getLeadActivities = (leadId) =>
  adminApi.getOutreachActivities({ entity_id: leadId, entity_type: 'lead' });

adminApi.getOpportunityActivities = (opportunityId) =>
  adminApi.getOutreachActivities({ entity_id: opportunityId, entity_type: 'opportunity' });

adminApi.getOutreachActivity = (activityId) => adminApi.get(`/admin/sales/outreach-activities/${activityId}/`);
adminApi.createOutreachActivity = (activityData) => adminApi.post('/admin/sales/outreach-activities/', activityData);
adminApi.updateOutreachActivity = (activityId, activityData) => adminApi.patch(`/admin/sales/outreach-activities/${activityId}/`, activityData);
adminApi.deleteOutreachActivity = (activityId) => adminApi.delete(`/admin/sales/outreach-activities/${activityId}/`);

// Email Tracking & Management APIs
adminApi.getEmailActivity = (filters = {}) => adminApi.get('/admin/sales/emails/activity/', { params: filters });

// Job Listings APIs (RapidAPI Provider)
adminApi.getJobEndpoints = () => adminApi.get('/admin/sales/job-endpoints/');
adminApi.getJobListings = (filters = {}) => adminApi.get('/admin/sales/job-listings/', { params: filters });
adminApi.getJobListingDetails = (jobId, filters = {}) => adminApi.get(`/admin/sales/job-listings/${jobId}/`, { params: filters });

// Retell Analytics API
adminApi.getRetellAnalytics = (query) => adminApi.post('/admin/sales/analytics/', query);
// Retell Dashboard API (new consolidated endpoint)
adminApi.getRetellConsolidatedData = (params) => adminApi.get('/admin/sales/analytics/consolidated-data/', { params });

// Voice AI Provider (RetellAI) Authentication API
adminApi.voiceaiLogin = (credentials) => adminApi.post('/voiceai/login', credentials);
adminApi.voiceaiSelectMFAMethod = (data) => adminApi.post('/voiceai/select-mfa-method', data);
adminApi.voiceaiVerifyOTP = (data) => adminApi.post('/voiceai/otp', data);
adminApi.voiceaiSessionStatus = () => adminApi.get('/voiceai/session-status');
adminApi.voiceaiLogout = () => adminApi.post('/voiceai/logout');
adminApi.voiceaiGetOrganizations = () => adminApi.get('/voiceai/organizations');
adminApi.voiceaiGetAnalytics = (params) => adminApi.get('/voiceai/analytics', { params });
adminApi.voiceaiGetCredentials = () => adminApi.get('/voiceai/credentials');
adminApi.voiceaiSaveCredentials = (data) => adminApi.post('/voiceai/credentials', data);
adminApi.voiceaiUpdateCredential = (data) => adminApi.patch('/voiceai/credentials', data);
adminApi.voiceaiDeleteCredential = (data) => adminApi.delete('/voiceai/credentials', { data });

// Log test mode status
if (isTestMode) {
  console.log('[TEST MODE] API requests will be logged to console instead of sent to server');
}
// Trigger transcription for audio session or segment
adminApi.triggerTranscription = (sessionId, segmentId = null) => {
  const body = {
    session_id: sessionId
  };
  if (segmentId) {
    body.segment_id = segmentId;
  }
  return adminApi.post('/provider/audio/transcription/trigger/', body);
};

// User Management APIs
adminApi.getUsers = (params) => adminApi.get('/admin/users/', { params });
adminApi.getUserDetails = (userId) => adminApi.get(`/admin/users/${userId}/`);
adminApi.updateUser = (userId, data) => adminApi.patch(`/admin/users/${userId}/`, data);
adminApi.deleteUser = (userId) => adminApi.delete(`/admin/users/${userId}/`);
adminApi.getRoles = (params) => adminApi.get('/admin/roles/', { params });

// User Profile Management APIs (Memberships, EHR, SSO)
adminApi.getUserMemberships = (userId) => adminApi.get(`/admin/users/${userId}/memberships/`);
adminApi.createUserMembership = (userId, data) => adminApi.post(`/admin/users/${userId}/memberships/`, data);
adminApi.updateUserMembership = (userId, membershipId, data) => adminApi.put(`/admin/users/${userId}/memberships/${membershipId}/`, data);
adminApi.deleteUserMembership = (userId, membershipId) => adminApi.delete(`/admin/users/${userId}/memberships/${membershipId}/`);

adminApi.getUserEhrProfiles = (userId) => adminApi.get(`/admin/users/${userId}/ehr-profiles/`);
adminApi.createUserEhrProfile = (userId, data) => adminApi.post(`/admin/users/${userId}/ehr-profiles/`, data);
adminApi.updateUserEhrProfile = (userId, profileId, data) => adminApi.put(`/admin/users/${userId}/ehr-profiles/${profileId}/`, data);
adminApi.deleteUserEhrProfile = (userId, profileId) => adminApi.delete(`/admin/users/${userId}/ehr-profiles/${profileId}/`);

adminApi.getUserSsoConfigs = (userId) => adminApi.get(`/admin/users/${userId}/sso-configs/`);
adminApi.createUserSsoConfig = (userId, data) => adminApi.post(`/admin/users/${userId}/sso-configs/`, data);
adminApi.updateUserSsoConfig = (userId, configId, data) => adminApi.put(`/admin/users/${userId}/sso-configs/${configId}/`, data);
adminApi.deleteUserSsoConfig = (userId, configId) => adminApi.delete(`/admin/users/${userId}/sso-configs/${configId}/`);

// User Preferences APIs (UI settings: filters, column layouts, etc.)
adminApi.getUserPreferences = () => adminApi.get('/admin/user/preferences/');
adminApi.updateUserPreferences = (preferences) => adminApi.put('/admin/user/preferences/', { preferences });

// Sales Module APIs
adminApi.getSalesUsers = (params) => adminApi.get('/admin/sales/users/', { params });
adminApi.getSalesUserDetails = (userId) => adminApi.get(`/admin/sales/users/${userId}/`);
adminApi.createSalesUser = (data) => adminApi.post('/admin/sales/users/', data);
adminApi.updateSalesUser = (userId, data) => adminApi.patch(`/admin/sales/users/${userId}/`, data);
adminApi.deleteSalesUser = (userId) => adminApi.delete(`/admin/sales/users/${userId}/`);
adminApi.getOrganizations = (params) => adminApi.get('/admin/sales/organizations/', { params });
adminApi.getOrganizationDetails = (orgId) => adminApi.get(`/admin/sales/organizations/${orgId}/`);
adminApi.createOrganization = (data) => adminApi.post('/admin/sales/organizations/', data);
adminApi.updateOrganization = (orgId, data) => adminApi.patch(`/admin/sales/organizations/${orgId}/`, data);
adminApi.deleteOrganization = (orgId) => adminApi.delete(`/admin/sales/organizations/${orgId}/`);
adminApi.getSalesTeams = (params) => adminApi.get('/admin/sales/teams/', { params });
adminApi.getSalesTeamDetails = (teamId) => adminApi.get(`/admin/sales/teams/${teamId}/`);
adminApi.createSalesTeam = (data) => adminApi.post('/admin/sales/teams/', data);
adminApi.updateSalesTeam = (teamId, data) => adminApi.patch(`/admin/sales/teams/${teamId}/`, data);
adminApi.deleteSalesTeam = (teamId) => adminApi.delete(`/admin/sales/teams/${teamId}/`);
adminApi.getOrganizationMemberships = (params) => adminApi.get('/admin/sales/organizations/memberships/', { params });
adminApi.createOrganizationMembership = (data) => adminApi.post('/admin/sales/organizations/memberships/', data);
adminApi.deleteOrganizationMembership = (membershipId) => adminApi.delete(`/admin/sales/organizations/memberships/${membershipId}/`);

// Public API endpoints (no authentication required)
// Get version information from backend with localStorage caching
export const publicApi = {
  getVersion: async () => {
    const cachedVersion = localStorage.getItem('app_version');
    if (cachedVersion) return cachedVersion;    
    try {
      const publicClient = axios.create({
        baseURL: useNginxProxy ? '/api' : `${API_BASE_URL}/api`,
        headers: {
          'Content-Type': 'application/json',
        },
      });
      const response = await publicClient.get('/version/');
      const version = (response.data?.['mira-pin-frontend']?.version || '').replace(/\s*\(\$\{GIT_COMMIT_SHA\}\)/g, '');
      if (version) {
        localStorage.setItem('app_version', version);
      }
      return version;
    } catch (error) {
      return '';
    }
  },
  // Refresh version (clears cache and fetches fresh)
  refreshVersion: async () => {
    localStorage.removeItem('app_version');
    return publicApi.getVersion();
  }
};

export default apiClient;